import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useV2Query } from 'utils';
import { NullState } from 'bench-internal-theme';
import { WrappedSpinner } from '../../../spinner';
import { GET_COMPANY_DETAILS } from './queries';
import {
  CompanyDetailsData,
  PersonRelationship,
  TaxPreparerInfo,
  AlternateContactInfo,
} from './types';
import getClientId from '../../getClientId';
import AboutBusinessSection from './components/AboutBusinessSection';
import TaxDetailsSection from './components/TaxDetailsSection';
import PreferencesSection from './components/PreferencesSection';
import AccountsToTrackSection from './components/AccountsToTrackSection';

const CompanyDetailsLayout = styled.div`
  display: grid;
  gap: 16px;
  margin: 10px auto;
  margin-bottom: 50px;
  max-width: 900px;
  padding: 0 10px;
`;

const extractTaxPreparer = (relationships?: PersonRelationship[]): TaxPreparerInfo | undefined => {
  if (!relationships) return undefined;
  const taxPreparerRel = relationships.find(
    rel => rel.role === 'ACCOUNTANT_WITH_ACCESS' || rel.role === 'ACCOUNTANT_WITHOUT_ACCESS',
  );
  if (!taxPreparerRel) return undefined;
  return {
    fullName: taxPreparerRel.person.fullName,
    email: taxPreparerRel.person.email,
    hasAccessToBench: taxPreparerRel.role === 'ACCOUNTANT_WITH_ACCESS',
  };
};

const extractAlternateContact = (
  relationships?: PersonRelationship[],
): AlternateContactInfo | undefined => {
  if (!relationships) return undefined;
  const contactRel = relationships.find(rel => rel.role === 'ALTERNATE_CONTACT');
  if (!contactRel) return undefined;
  return {
    fullName: contactRel.person.fullName,
    email: contactRel.person.email,
    phone: contactRel.person.phone,
  };
};

const CompanyDetailsContent: React.FC = () => {
  const clientId = useMemo(() => getClientId(), []);

  const { data, loading, error } = useV2Query<CompanyDetailsData>(GET_COMPANY_DETAILS, {
    variables: { clientId },
    fetchPolicy: 'cache-first',
  });

  if (error) {
    return (
      <NullState
        bigEmoji="🙈"
        title="Uh oh! An error was encountered while fetching company details."
      />
    );
  }

  if (loading) {
    return <WrappedSpinner />;
  }

  const client = data?.client;
  const onboarding = client?.clientInfo?.onboarding;

  const taxPreparer = extractTaxPreparer(client?.personRelationships);
  const alternateContact = extractAlternateContact(client?.personRelationships);

  return (
    <CompanyDetailsLayout>
      <AboutBusinessSection
        legalBusinessName={client?.businessProfile?.legalBusinessName}
        dba={client?.businessName}
        industry={client?.industry}
        yearFounded={client?.businessProfile?.yearFounded}
        equityHolders={client?.clientInfo?.equityHolders}
      />

      <TaxDetailsSection
        taxClientStatus={client?.taxClientStatus}
        filingType={client?.filingType}
        fiscalYearEnd={client?.fiscalYearEnd}
        taxPreparer={taxPreparer}
        filesDelawareFranchiseTax={client?.businessProfile?.filesDelawareFranchiseTax}
        track1099={onboarding?.track1099}
      />

      <PreferencesSection
        milestones={client?.businessProfile?.milestones}
        alternateContact={alternateContact}
      />

      <AccountsToTrackSection
        financialAccounts={onboarding?.financialAccounts}
        transferApps={onboarding?.transferApps}
        loansAndLeases={onboarding?.loansAndLeases}
        revenueCollection={onboarding?.revenueCollection}
        payroll={onboarding?.payroll}
        other={onboarding?.other}
        accountsToTrackComplete={onboarding?.accountsToTrackComplete}
      />
    </CompanyDetailsLayout>
  );
};

export default CompanyDetailsContent;
